import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Heart, CheckCircle2, Clock, Mail, Utensils, Image, LogOut, Info as InfoIcon } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const ITEMS = (t) => [
  { to: "/", icon: Heart, label: t.nav.inicio, id: "inicio" },
  { to: "/asistencia", icon: CheckCircle2, label: t.nav.asistencia, id: "asistencia" },
  { to: "/programa", icon: Clock, label: t.nav.programa, id: "programa" },
  { to: "/info", icon: InfoIcon, label: t.nav.info, id: "info" },
  { to: "/mensajes", icon: Mail, label: t.nav.mensajes, id: "mensajes" },
  { to: "/menu", icon: Utensils, label: t.nav.menu, id: "menu" },
  { to: "/galeria", icon: Image, label: t.nav.galeria, id: "galeria" },
];

export default function TopNav() {
  const { lang, setLang, t } = useI18n();
  const { guest, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isAdmin = guest?.role === "admin";

  return (
    <header
      data-testid="top-nav"
      className="sticky top-0 z-50 bg-[color:var(--bg-blush)]/90 backdrop-blur-md border-b border-[color:var(--line)]"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
        <NavLink to="/" className="flex flex-col items-start leading-none shrink-0" data-testid="nav-logo">
          <span className="font-script text-xl sm:text-3xl text-[color:var(--ink-wine)]">Selena&Mario</span>
          <span className="font-sans text-[0.55rem] sm:text-[0.65rem] tracking-[0.3em] sm:tracking-[0.35em] text-[color:var(--ink-wine)] font-semibold mt-0.5">
            18 · 07 · 2026
          </span>
        </NavLink>

        <nav className="flex items-center gap-0.5 sm:gap-1.5" data-testid="desktop-nav">
          {ITEMS(t).map(({ to, icon: Icon, label, id }) => (
            <NavLink
              key={id}
              to={to}
              end={to === "/"}
              data-testid={`nav-${id}`}
              title={label}
              className={({ isActive }) =>
                `flex items-center gap-2 px-2 sm:px-4 py-2 rounded-full text-xs tracking-[0.18em] uppercase font-semibold transition-all ${
                  isActive
                    ? "bg-[color:var(--bg-paper)] text-[color:var(--ink-wine)] shadow-sm"
                    : "text-[color:var(--ink-wine)]/70 hover:text-[color:var(--ink-wine)] hover:bg-[color:var(--bg-paper)]/60"
                }`
              }
            >
              <Icon size={18} strokeWidth={2} />
              <span className="hidden xl:inline">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "es" ? "ca" : "es")}
            className="inline-flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-full border border-[color:var(--line)] text-[0.65rem] sm:text-[0.7rem] font-semibold tracking-[0.15em] sm:tracking-[0.18em] text-[color:var(--ink-wine)] hover:border-[color:var(--ink-wine)] transition-colors"
            aria-label="Toggle language"
          >
            <span className={lang === "es" ? "opacity-100" : "opacity-40"}>ES</span>
            <span className="opacity-30">·</span>
            <span className={lang === "ca" ? "opacity-100" : "opacity-40"}>CA</span>
          </button>
          <button
            data-testid="auth-toggle"
            onClick={handleLogout}
            title={t.gate.logout}
            aria-label={t.gate.logout}
            className={`inline-flex items-center justify-center gap-1.5 w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 rounded-full border text-[0.65rem] sm:text-[0.7rem] font-semibold tracking-[0.15em] sm:tracking-[0.18em] uppercase transition-colors ${
              isAdmin
                ? "bg-[color:var(--ink-wine)] text-white border-[color:var(--ink-wine)]"
                : "border-[color:var(--ink-wine)] text-[color:var(--ink-wine)] hover:bg-[color:var(--ink-wine)] hover:text-white"
            }`}
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">{t.gate.logout}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
